import * as React from "react";
import { toast } from "sonner";
import { Send } from "lucide-react";

import { api, formatApiErrorDetail } from "@/lib/api";
import EntityAvatar from "@/components/identity/EntityAvatar";

/**
 * DirectoryPanel — the content of the on-shift directory. Shared by:
 * - `DirectoryDrawer` (mobile / < xl width)
 * - `DirectoryRail`   (persistent right rail on ≥ xl)
 *
 * Both wrappers pass down `onOpenConversation` and get the raw data.
 * Data refetches every 60s while mounted (light polling — the SSE
 * events don't yet carry roster changes; a lightweight tick is enough).
 */
export default function DirectoryPanel({ companyId, canPost, onOpenConversation, testidPrefix = "directory" }) {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const { data } = await api.get(`/companies/${companyId}/comms/directory`);
      setData(data);
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't load directory");
    } finally { setLoading(false); }
  }, [companyId]);

  React.useEffect(() => {
    load();
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, [load]);

  async function messageDuty(siteId, priority = "normal") {
    const body = window.prompt("Message the duty manager:", "");
    if (!body || !body.trim()) return;
    try {
      const { data } = await api.post(`/companies/${companyId}/comms/duty-manager/message`, {
        site_id: siteId, body: body.trim(), priority,
      });
      toast.success(`Delivered to ${data.resolved_duty_manager.name || "duty manager"}`);
      if (onOpenConversation) onOpenConversation(data.conversation_id);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't send");
    }
  }

  if (loading) {
    return (
      <div className="p-4 space-y-3" data-testid={`${testidPrefix}-loading`}>
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="card-panel p-4">
            <div className="h-4 w-32 bg-brand-hush rounded animate-pulse mb-2" />
            <div className="h-3 w-full bg-brand-hush rounded animate-pulse mb-1" />
            <div className="h-3 w-3/4 bg-brand-hush rounded animate-pulse" />
          </div>
        ))}
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-4" data-testid={`${testidPrefix}-error`}>
        <div className="text-[13px] font-semibold text-brand-ink">Couldn't load who's on shift</div>
        <div className="text-[12px] text-brand-muted mt-1 mb-3">{error}</div>
        <button className="btn-outline text-[12px] h-8" onClick={load}>Try again</button>
      </div>
    );
  }
  if (!data || data.sites.length === 0) {
    return (
      <div className="p-6 text-center" data-testid={`${testidPrefix}-empty`}>
        <div className="text-[13.5px] font-semibold text-brand-ink">No one's on shift right now</div>
        <div className="text-[12px] text-brand-muted mt-1">
          Once shifts are published for the current time, everyone rostered will appear here.
        </div>
      </div>
    );
  }
  return (
    <div className="p-4 space-y-4">
      {data.sites.map((s) => (
        <div key={s.site_id} className="card-panel" data-testid={`${testidPrefix}-site-${s.site_id}`}>
          <div className="px-4 py-3 border-b border-brand-rule flex items-center gap-2">
            <EntityAvatar kind="site" name={s.site_name} id={s.site_id} size={24} />
            <div className="text-[13.5px] font-semibold text-brand-ink flex-1 truncate">{s.site_name}</div>
            <span className="chip chip-violet mono-num text-[10.5px]">
              {s.on_shift.length} on shift
            </span>
          </div>
          {s.duty_manager && (
            <div className="px-4 py-3 border-b border-brand-rule flex items-center gap-3">
              <EntityAvatar kind="person" name={s.duty_manager.name} id={s.duty_manager.user_id} size={30} />
              <div className="min-w-0 flex-1">
                <div className="text-[10.5px] uppercase tracking-wider text-brand-muted">Duty manager</div>
                <div className="text-[13px] font-semibold text-brand-ink truncate">
                  {s.duty_manager.name}
                </div>
                <div className="text-[10.5px] text-brand-muted">
                  resolved via {(s.duty_manager.reason || "").replace(/_/g, " ")}
                </div>
              </div>
              {canPost && (
                <button
                  className="btn-violet h-8 text-[11.5px]"
                  onClick={() => messageDuty(s.site_id, "normal")}
                  data-testid={`${testidPrefix}-duty-msg-${s.site_id}`}
                >
                  <Send size={12} /> Message
                </button>
              )}
            </div>
          )}
          <div className="p-3 grid gap-1.5">
            {s.on_shift.map((p) => (
              <div key={`${s.site_id}-${p.user_id}`} className="flex items-center gap-2.5 px-1.5 py-1 rounded hover:bg-brand-hush">
                <EntityAvatar kind="person" name={p.name} id={p.user_id} size={22} />
                <div className="min-w-0 flex-1">
                  <div className="text-[12.5px] font-medium text-brand-ink truncate">{p.name}</div>
                  <div className="text-[10.5px] text-brand-muted truncate">
                    {p.role || "on shift"} · until {new Date(p.end).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
